import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  Building2, 
  DollarSign,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Card  from '@/components/ui/Card';
import Button  from '../../ui/Button';
import ApiService from '../../../services/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30d');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_API_URL}/api/admin/dashboard/stats`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
          }
        });
        const data = await response.json();
        setStats(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchDashboardData();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await ApiService.getAdminDashboardStats(timeframe);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Dashboard Overview</h2>
        <div className="flex gap-2">
          {['7d', '30d', '90d', '1y'].map((period) => (
            <Button
              key={period}
              variant={timeframe === period ? 'default' : 'outline'}
              onClick={() => setTimeframe(period)}
            >
              {period}
            </Button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* MRR */}
        <Card className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
              <h3 className="text-2xl font-bold mt-2">${stats.mrr.toLocaleString()}</h3>
              <div className={`flex items-center mt-2 ${
                stats.mrrGrowth >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {stats.mrrGrowth >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                <span className="text-sm">{Math.abs(stats.mrrGrowth)}% from last month</span>
              </div>
            </div>
            <div className="p-3 bg-primary-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </Card>

        {/* Total Businesses */}
        <Card className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Businesses</p>
              <h3 className="text-2xl font-bold mt-2">{stats.activeBusinesses}</h3>
              <div className={`flex items-center mt-2 ${
                stats.businessGrowth >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {stats.businessGrowth >= 0 ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                <span className="text-sm">{Math.abs(stats.businessGrowth)} new this month</span>
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        {/* Total Users */}
        <Card className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <h3 className="text-2xl font-bold mt-2">{stats.totalUsers}</h3>
              <div className="text-gray-600 text-sm mt-2">
                Across {stats.activeBusinesses} businesses
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>

        {/* Growth Rate */}
        <Card className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-gray-600">Growth Rate</p>
              <h3 className="text-2xl font-bold mt-2">{stats.growthRate}%</h3>
              <div className="text-gray-600 text-sm mt-2">
                Month over month
              </div>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity and Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Revenue Growth</h3>
          <div className="h-64">
            {/* Add revenue chart component here */}
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {stats.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <activity.icon className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm">{activity.description}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity.timestamp).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Businesses */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top Businesses</h3>
          <div className="space-y-4">
            {stats.topBusinesses.map((business) => (
              <div key={business.id} className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-700 font-medium">
                      {business.name.charAt(0)}
                    </span>
                  </div>
                  <span className="font-medium">{business.name}</span>
                </div>
                <span className="text-gray-600">${business.mrr}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Plan Distribution */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Plan Distribution</h3>
          <div className="space-y-4">
            {stats.planDistribution.map((plan) => (
              <div key={plan.name}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{plan.name}</span>
                  <span>{plan.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full"
                    style={{ width: `${plan.percentage}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Customer Support Stats */}

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Support Metrics</h3>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Average Response Time</p>
              <p className="text-xl font-semibold">{stats.supportMetrics.avgResponseTime} minutes</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Satisfaction Rate</p>
              <p className="text-xl font-semibold">{stats.supportMetrics.satisfactionRate}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Open Tickets</p>
              <p className="text-xl font-semibold">{stats.supportMetrics.openTickets}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* System Status */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">System Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.systemStatus.map((service) => (
            <div key={service.name} className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${
                service.status === 'operational' ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <div>
                <p className="font-medium">{service.name}</p>
                <p className="text-sm text-gray-500">{service.uptime}% uptime</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;